import { writable, get } from 'svelte/store';
import { supabase } from '$lib/supabase';
import { authStore } from './authStore';

interface FavoriteState {
    favorites: string[];
    isLoading: boolean;
}

function createFavoriteStore() {
    const { subscribe, set, update } = writable<FavoriteState>({
        favorites: [],
        isLoading: false
    });

    return {
        subscribe,
        // お気に入りの初期化
        async initializeFavorites(videoId: string) {
            update(state => ({ ...state, isLoading: true }));
            
            const currentUser = get(authStore).user;
            if (!currentUser) {
                set({ favorites: [], isLoading: false });
                return;
            }

            const { data: favoriteData } = await supabase
                .from('favorites')
                .select('video_id, created_at')
                .eq('user_id', currentUser.id)
                .order('created_at', { ascending: false });

            if (favoriteData) {
                const favorites = favoriteData.map(f => f.video_id);
                set({ favorites, isLoading: false });
            } else {
                set({ favorites: [], isLoading: false });
            }
        },

        // お気に入りの切り替え
        async toggleFavorite(videoDetails: any) {
            const currentUser = get(authStore).user;
            if (!currentUser) return false;

            try {
                const isFavorite = await this.isFavorite(videoDetails.id);

                if (isFavorite) {
                    // お気に入りから削除
                    const { error } = await supabase
                        .from('favorites')
                        .delete()
                        .eq('user_id', currentUser.id)
                        .eq('video_id', videoDetails.id);

                    if (error) throw error;

                    update(state => ({
                        ...state,
                        favorites: state.favorites.filter(id => id !== videoDetails.id)
                    }));
                } else {
                    // お気に入りに追加
                    const { error } = await supabase
                        .from('favorites')
                        .insert({
                            user_id: currentUser.id,
                            video_id: videoDetails.id
                        });

                    if (error) throw error;

                    update(state => ({
                        ...state,
                        favorites: [...state.favorites, videoDetails.id]
                    }));
                }

                return !isFavorite;
            } catch (error) {
                console.error('Error toggling favorite:', error);
                return null;
            }
        },

        // お気に入り状態の確認
        async isFavorite(videoId: string): Promise<boolean> {
            return this.getFavorites().includes(videoId);
        },

        // 現在のお気に入りリストを取得
        getFavorites() {
            let favorites: string[] = [];
            subscribe(state => {
                favorites = state.favorites;
            })();
            return favorites;
        },

        // ストアのリセット
        reset() {
            set({ favorites: [], isLoading: false });
        }
    };
}

export const favoriteStore = createFavoriteStore(); 